import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlaybackService } from '../../../core/services/playback.service';
import { WebPlaybackTrack } from '../../../shared/models/spotify.models';
import { ExpandedPlayerDialogComponent } from './expanded-player-dialog/expanded-player-dialog.component';
import { DeviceSelectorDialogComponent } from './device-selector-dialog/device-selector-dialog.component';
import { CastService } from '../../../core/services/cast.service';
import { AIService } from '../../../core/services/ai.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-floating-player',
  templateUrl: './floating-player.component.html',
  styleUrls: ['./floating-player.component.scss']
})
export class FloatingPlayerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private progressInterval: any;
  private deviceCheckInterval: any;

  // State properties
  currentTrack: WebPlaybackTrack | null = null;
  isPlaying: boolean = false;
  progress: number = 0;
  duration: number = 0;
  isPlayerReady: boolean = false;
  isPremium: boolean = false;
  hasOtherDevices: boolean = false;
  isWebSDKActive: boolean = false;
  isRemoteStale = false; // another device claims playback but is dead

  constructor(
    private playbackService: PlaybackService,
    private castService: CastService,
    private dialog: MatDialog,
    private aiService: AIService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscribeToPlaybackState();
    this.startProgressTracking();
    this.startDeviceChecking();
    this.autoConnectToSpotify();

    // Start centralized cross-device polling
    this.playbackService.startCrossDevicePolling();
  }

  ngOnDestroy(): void {
    this.stopProgressTracking();
    this.stopDeviceChecking();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToPlaybackState(): void {
    // Notices from automatic recovery (e.g. it gave up and wants a Play tap).
    // The floating player is always mounted, so it owns showing them.
    this.playbackService.playbackNotice$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => this.snackBar.open(message, 'Close', { duration: 8000 }));

    // Subscribe to player ready state
    this.playbackService.isReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isReady => {
        this.isPlayerReady = isReady;
      });

    // Subscribe to premium status
    this.playbackService.isPremium$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isPremium => {
        this.isPremium = isPremium;
      });

    // Subscribe to combined playback state (SDK + cross-device polling)
    this.playbackService.combinedPlaybackState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state) {
          this.currentTrack = state.currentTrack;
          this.isPlaying = state.isPlaying;
          this.progress = state.progress;
          this.duration = state.duration;
          this.isWebSDKActive = state.isWebSDKActive;
          this.isRemoteStale = state.isStale === true;
        } else {
          // Nothing playing anywhere. Keeping the old track with isPlaying=true
          // showed pause buttons for silence and fed the expanded player a
          // stale view (Oct 13 report: gray screen).
          this.currentTrack = null;
          this.isPlaying = false;
          this.isRemoteStale = false;
          this.progress = 0;
          this.duration = 0;
        }
      });
  }

  private startProgressTracking(): void {
    this.stopProgressTracking();
    
    this.progressInterval = setInterval(async () => {
      try {
        if (this.isPlaying && this.isPlayerReady) {
          const state = await this.playbackService.getCurrentState();
          if (state && !state.paused) {
            this.progress = state.position;
            this.duration = state.duration;
          }
        }
      } catch (error) {
        console.warn('Error getting current state for progress tracking:', error);
      }
    }, 1000);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private startDeviceChecking(): void {
    this.stopDeviceChecking();
    
    // Check immediately
    this.checkForOtherDevices();
    
    // Then check every 30 seconds
    this.deviceCheckInterval = setInterval(() => {
      this.checkForOtherDevices();
    }, 30000);
  }

  private stopDeviceChecking(): void {
    if (this.deviceCheckInterval) {
      clearInterval(this.deviceCheckInterval);
      this.deviceCheckInterval = null;
    }
  }

  private async checkForOtherDevices(): Promise<void> {
    if (!this.isPlayerReady || !this.isPremium) {
      this.hasOtherDevices = false;
      return;
    }

    try {
      const response = await this.playbackService.getAvailableDevices();
      const hasSpotifyDevices = response.devices.some(device => !device.is_active);
      const hasCastDevices = this.castService.isAvailable() && !this.castService.isConnected();
      
      // Check if there are any non-active devices (other devices we can cast to)
      this.hasOtherDevices = hasSpotifyDevices || hasCastDevices;
      
      // Check if Web SDK is the active device
      const webSDKDeviceId = this.playbackService.getDeviceId();
      const activeDevice = response.devices.find(device => device.is_active);
      this.isWebSDKActive = !!activeDevice && activeDevice.id === webSDKDeviceId;
    } catch (error) {
      console.warn('Failed to check for other devices:', error);
      this.hasOtherDevices = false;
    }
  }

  // Control methods
  async togglePlay(): Promise<void> {
    if (this.isPlayerReady && this.isWebSDKActive) {
      // Local player is the active device (togglePlay itself pauses via the
      // Web API or takes over when it has nothing loaded).
      try {
        await this.playbackService.togglePlay();
      } catch (error) {
        console.error('Floating player toggle play error:', error);
        this.snackBar.open('Playback control failed', 'Close', { duration: 4000 });
      }
      return;
    }
    if (this.isRemoteStale || !this.currentTrack) {
      // The "playing" device is dead, or nothing is loaded anywhere — start here.
      await this.takeOverWithFeedback();
      return;
    }
    // Control the remote device through the backend.
    const wantsPlay = !this.isPlaying;
    try {
      if (wantsPlay) {
        const response = await fetch(`${environment.apiUrl}/spotify/player/play`, {
          method: 'PUT',
          credentials: 'include',
          headers: this.getFetchHeaders(),
          body: JSON.stringify({})
        });
        if (!response.ok) {
          throw new Error(`Remote play failed (${response.status})`);
        }
      } else {
        // Through the service so our own intent / pending auto-resume can't
        // restart the music afterwards.
        await this.playbackService.pauseActiveDevice();
      }
      // Update state immediately for better UX
      this.isPlaying = wantsPlay;
    } catch (error) {
      if (wantsPlay) {
        // The remote device didn't answer a PLAY — it's gone (Oct 13: a dead
        // tab). Start here rather than leaving the press doing nothing.
        console.warn('Floating player remote play failed, taking over here:', error);
        await this.takeOverWithFeedback();
      } else {
        // A failed PAUSE must never start music on this phone.
        console.warn('Floating player remote pause failed:', error);
        this.snackBar.open('Could not pause the playing device', 'Close', { duration: 4000 });
      }
    }
  }

  private async takeOverWithFeedback(): Promise<void> {
    try {
      await this.playbackService.takeOverPlayback();
    } catch (error) {
      console.error('Floating player takeover failed:', error);
      this.snackBar.open('Could not start playback on this device', 'Close', { duration: 4000 });
    }
  }

  async previousTrack(): Promise<void> {
    try {
      if (this.isPlayerReady && this.isWebSDKActive) {
        // Use Web Playback SDK only if it's the active device
        await this.playbackService.previousTrack();
      } else {
        // Use backend API for remote devices or when SDK is not active
        const response = await fetch(`${environment.apiUrl}/spotify/player/previous`, {
          method: 'POST',
          credentials: 'include',
          headers: this.getFetchHeaders(),
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error('Failed to skip to previous track');
        }
      }
    } catch (error) {
      console.error('Floating player previous track error:', error);
      this.snackBar.open('Could not reach the playing device — press play to move playback here', 'Close', { duration: 4000 });
    }
  }

  async nextTrack(): Promise<void> {
    try {
      if (this.isPlayerReady && this.isWebSDKActive) {
        // Use Web Playback SDK only if it's the active device
        await this.playbackService.nextTrack();
      } else {
        // Use backend API for remote devices or when SDK is not active
        const response = await fetch(`${environment.apiUrl}/spotify/player/next`, {
          method: 'POST',
          credentials: 'include',
          headers: this.getFetchHeaders(),
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error('Failed to skip to next track');
        }
      }
    } catch (error) {
      console.error('Floating player next track error:', error);
      this.snackBar.open('Could not reach the playing device — press play to move playback here', 'Close', { duration: 4000 });
    }
  }


  // Expand player dialog
  expandPlayer(): void {
    this.dialog.open(ExpandedPlayerDialogComponent, {
      width: '90vw',
      maxWidth: '800px',
      height: '90vh',
      maxHeight: '600px',
      panelClass: 'expanded-player-dialog',
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'expanded-player-backdrop'
    });
  }

  // Utility methods
  getArtistNames(): string {
    if (!this.currentTrack?.artists) return '';
    return this.currentTrack.artists.map(artist => artist.name).join(', ');
  }

  getAlbumImage(): string | null {
    if (!this.currentTrack?.album?.images || this.currentTrack.album.images.length === 0) {
      return null;
    }
    
    // Return the smallest image for floating player
    const sortedImages = this.currentTrack.album.images.sort((a, b) => (a.width || 0) - (b.width || 0));
    return sortedImages[0]?.url || null;
  }

  formatTime(ms: number): string {
    return this.playbackService.formatDuration(ms);
  }

  getCurrentTimeFormatted(): string {
    return this.formatTime(this.progress);
  }

  getTotalTimeFormatted(): string {
    return this.formatTime(this.duration);
  }

  // State getters for template
  get shouldShow(): boolean {
    // Show if we have a current track AND something is playing
    return !!this.currentTrack && this.isPlaying;
  }

  get hasTrack(): boolean {
    return !!this.currentTrack;
  }

  async generateSimilarPlaylist(): Promise<void> {
    if (!this.currentTrack) return;

    try {
      const prompt = `Create a Dynamic Playlist with songs similar to "${this.currentTrack.name}" by ${this.getArtistNames()}. Generate songs with similar style, genre, tempo, and mood.`;
      
      this.snackBar.open('Generating Dynamic Playlist...', 'Close', { duration: 3000 });
      
      const result = await this.aiService.generateAndCreatePlaylist({
        prompt: prompt
      }, false, 'Dynamic Playlist', `Songs similar to ${this.currentTrack.name}`);

      // Automatically navigate to the new playlist
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/playlists', result.playlist.id]);
      });

      this.snackBar.open(`"${result.playlist.name}" created successfully!`, 'Dismiss', { 
        duration: 3000
      });

    } catch (error: any) {
      console.error('Error generating similar playlist:', error);
      this.snackBar.open(error.message || 'Failed to generate playlist', 'Close', { duration: 5000 });
    }
  }

  openDeviceSelector(): void {
    const dialogRef = this.dialog.open(DeviceSelectorDialogComponent, {
      width: '400px',
      maxWidth: '95vw',
      maxHeight: '80vh',
      panelClass: 'device-selector-dialog-panel',
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'device-selector-backdrop'
    });

    dialogRef.afterClosed().subscribe(async (device) => {
      if (device) {
        try {
          await this.playbackService.transferPlayback(device.id, true);
          this.snackBar.open(`Switched to ${device.name}`, 'Close', { duration: 2000 });
        } catch (error) {
          console.error('Failed to transfer playback:', error);
          this.snackBar.open('Failed to switch device', 'Close', { duration: 3000 });
        }
      }
    });
  }

  // Auto-connection to Spotify
  private async autoConnectToSpotify(): Promise<void> {
    try {
      console.log('Auto-connecting to Spotify...');

      // Wait a moment for auth to be fully ready
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Initialize the Spotify Web Playback SDK
      const success = await this.playbackService.initializePlayer();

      if (success) {
        console.log('Successfully auto-connected to Spotify');
      } else if (this.playbackService.sdkUnavailable) {
        console.log('Web Playback SDK unavailable (no EME support). Using remote playback only.');
      } else {
        console.log('Failed to auto-connect to Spotify, will retry in 30 seconds');
        // Retry after 30 seconds
        setTimeout(() => this.autoConnectToSpotify(), 30000);
      }
    } catch (error) {
      console.error('Error during auto-connection to Spotify:', error);
      // Don't retry if SDK is fundamentally unavailable
      if (!this.playbackService.sdkUnavailable) {
        setTimeout(() => this.autoConnectToSpotify(), 30000);
      }
    }
  }

  private getFetchHeaders(): Record<string, string> {
    return { 'Content-Type': 'application/json' };
  }
}